import React, { useEffect, useState } from "react";
import axios from "axios";
import { errorAlert, handleSuccess } from "../../Components/Alerts";
import TextField from "@mui/material/TextField";
import "../../Style/FiscalCashRegisterForm.scss";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";

/**
 * ATK Onboarding Inline Component (NOT Modal)
 * Direct integration with ATK endpoints
 * Expands inline below the form
 */
function ATKOnboardingWizard({ onComplete, branchId, initialATKPOSID }) {
  // Tab states
  const [activeTab, setActiveTab] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [environment, setEnvironment] = useState("test");
  const [isExpanded, setIsExpanded] = useState(false);

  // Tab 1 - Onboarding data
  const [nui, setNui] = useState("");
  const [fiscalizationNumber, setFiscalizationNumber] = useState("");
  const [posId, setPositId] = useState("");
  const [branchIdValue, setBranchIdValue] = useState(branchId || "");
  const [applicationId, setApplicationId] = useState("");
  const [sefCode, setSefCode] = useState("");

  // Tab 1 - Response from ATK
  const [verificationCode, setVerificationCode] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [verificationMessage, setVerificationMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // "success" | "error"

  // Tab 2 - Certificate data
  const [signedCertificate, setSignedCertificate] = useState("");
  const [csrMessage, setCSRMessage] = useState("");
  const [csrMessageType, setCSRMessageType] = useState("");

  // Tab 3 - Signature/Private Key data
  const [privateKeyPem, setPrivateKeyPem] = useState("");

  useEffect(() => {
    if (initialATKPOSID === undefined || initialATKPOSID === null || initialATKPOSID === "") {
      return;
    }

    setPositId(String(initialATKPOSID));
  }, [initialATKPOSID]);

  // Backend proxy URL (avoids CORS - backend calls ATK directly)
  const PROXY_BASE_URL = ApiLink.API_URL + "ATKProxy";

  /**
   * TAB 1: Send verify request to ATK
   */
  const handleVerify = async () => {
    if (!nui || !fiscalizationNumber || !posId || !applicationId || !sefCode) {
      errorAlert("Plotëso të gjitha fushat para se të vazhdosh.");
      return;
    }

    setIsLoading(true);
    setVerificationMessage("");
    setMessageType("");
    const headers = authHeader();
    try {
      const response = await axios.post(
        `${PROXY_BASE_URL}/verify/${nui}?env=${environment}`,
        {
          fiscalization_no: fiscalizationNumber,  // STRING per dokumentacion ATK!
          pos_id: Number(posId),
          branch_id: Number(branchIdValue),
          application_id: Number(applicationId),
        },
        { headers }
      );

      if (response.data) {
        console.log("ATK Verify response:", response.data);
        console.log("Response success flag:", response.data.success);
        
        // Handle different response formats from backend
        const verCode = response.data.verification_code || 
                        response.data.verificationCode || 
                        response.data.verCode || "";
        const busName = response.data.business_name || 
                        response.data.businessName || 
                        response.data.business_name || "";
        const atkMessage = response.data.message || "Verifikimi u krye me sukses!";
        const isSuccess = response.data.success || !!(verCode && verCode.length > 0);
        
        console.log("Extracted verification_code:", verCode);
        console.log("Extracted business_name:", busName);
        console.log("ATK Message:", atkMessage);
        console.log("Is Success:", isSuccess);
        
        if (!verCode || !isSuccess) {
          console.error("Verification failed! Response:", response.data);
          setVerificationMessage("❌ " + (atkMessage || "Verifikimi dështoi"));
          setMessageType("error");
        } else {
          setVerificationCode(verCode);
          setBusinessName(busName);
          setVerificationMessage("✅ " + atkMessage);
          setMessageType("success");
        }
      }
    } catch (error) {
      console.error("ATK Verify failed:", error?.response?.data || error);
      const errMsg = error?.response?.data?.message || 
                     (typeof error?.response?.data === "string" ? error.response.data : error.message);
      setVerificationMessage("❌ " + errMsg);
      setMessageType("error");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * TAB 2: Send CSR to ATK for signing
   */
  const handleSignCSR = async () => {
    if (!verificationCode) {
      errorAlert("Kryeje verifikimin në Hapi 1 para se të vazhdosh.");
      return;
    }

    setIsLoading(true);
    setCSRMessage("");
    setCSRMessageType("");
    const headers = authHeader();
    try {
      const response = await axios.post(
        `${PROXY_BASE_URL}/signcsr?env=${environment}`,
        {
          business_name: businessName,
          business_id: Number(nui),
          branch_id: Number(branchIdValue),
          verification_code: verificationCode,
          pos_id: Number(posId),
          application_id: Number(applicationId),
          sef_code: sefCode,
        },
        { headers }
      );

      if (response.data) {
        const signedCert = response.data.signed_certificate || "";
        const privKey = response.data.private_key_pem || "";
        const atkMessage = response.data.message || "Sertifikata u nënshkrua me sukses!";
        const isSuccess = response.data.success || !!(signedCert && privKey);
        
        console.log("SignCSR Response:", response.data);
        console.log("Has signed cert:", !!signedCert);
        console.log("Has private key:", !!privKey);
        console.log("Is Success:", isSuccess);
        
        if (!signedCert || !privKey || !isSuccess) {
          console.error("Certificate signing failed! Response:", response.data);
          setCSRMessage("❌ " + (atkMessage || "Nënshkrimi i sertifikatës dështoi"));
          setCSRMessageType("error");
        } else {
          setSignedCertificate(signedCert);
          setPrivateKeyPem(privKey);
          setCSRMessage("✅ " + atkMessage);
          setCSRMessageType("success");
        }
      }
    } catch (error) {
      console.error("ATK SignCSR failed:", error?.response?.data || error);
      const errMsg = error?.response?.data?.message || 
                     (typeof error?.response?.data === "string" ? error.response.data : error.message);
      setCSRMessage("❌ " + errMsg);
      setCSRMessageType("error");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * TAB 3: Complete onboarding and return data to parent form
   */
  const handleFinish = () => {
    if (!signedCertificate || !privateKeyPem) {
      errorAlert("Sertifikata ose çelësi privat nuk janë të plota.");
      return;
    }

    const onboardingData = {
      atkBranchID: branchIdValue,
      atkPOSID: posId,
      atkAppicationID: applicationId,
      atkFiscalizationNumber: fiscalizationNumber,
      atkSEFCode: sefCode,
      atkPrivateKeyPemEncrypted: privateKeyPem,
      atkSignedCertificatePem: signedCertificate,
    };

    onComplete(onboardingData);
    handleReset();
  };

  const handleReset = () => {
    setActiveTab(1);
    setNui("");
    setFiscalizationNumber("");
    setPositId("");
    setBranchIdValue(branchId || "");
    setApplicationId("");
    setSefCode("");
    setVerificationCode("");
    setBusinessName("");
    setVerificationMessage("");
    setMessageType("");
    setCSRMessage("");
    setCSRMessageType("");
    setSignedCertificate("");
    setPrivateKeyPem("");
    setIsExpanded(false);
  };

  return (
    <div className="atk-onboarding-inline" style={styles.container}>
      {/* Toggle Button */}
      <button
        type="button"
        className="atk-toggle-btn"
        onClick={() => setIsExpanded(!isExpanded)}
        style={styles.toggleButton}
      >
        <span style={{ fontSize: "18px", marginRight: "10px" }}>
          {isExpanded ? "▼" : "▶"}
        </span>
        🚀 Kryen Onboarding ATK
        {isExpanded && (
          <span style={{ marginLeft: "auto", fontSize: "12px", opacity: 0.7 }}>
            Hapi {activeTab}/3
          </span>
        )}
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div style={styles.expandedContent}>
          {/* Tab Navigation */}
          <div style={styles.tabNav}>
            {[1, 2, 3].map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                style={{
                  ...styles.tabButton,
                  ...(activeTab === tab ? styles.tabButtonActive : {}),
                }}
              >
                Hapi {tab}
              </button>
            ))}
          </div>

          {/* TAB 1: Onboarding/Verify */}
          {activeTab === 1 && (
            <div style={styles.tabContent}>
              <h5 style={styles.tabTitle}>Hapi 1: Verifikimi i të Dhënave Fiskale</h5>
              <p style={styles.tabDescription}>
                Plotëso identifikuesit e biznesit dhe POS-it për të kryer verifikimin me ATK.
              </p>

              {/* Environment Toggle */}
              <div style={{ marginBottom: "20px", width: "100%" }}>
                <div style={{ display: "flex", gap: "10px", justifyContent: "center", width: "100%", flexWrap: "nowrap" }}>
                  <button
                    type="button"
                    onClick={() => setEnvironment("test")}
                    disabled={isLoading}
                    style={{
                      flex: "0 0 auto",
                      padding: "10px 24px",
                      fontSize: "14px",
                      fontWeight: "600",
                      border: environment === "test" ? "none" : "2px solid #0d6efd",
                      borderRadius: "6px",
                      backgroundColor: environment === "test" ? "#0d6efd" : "transparent",
                      color: environment === "test" ? "white" : "#0d6efd",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    🧪 Test
                  </button>
                  <button
                    type="button"
                    onClick={() => setEnvironment("prod")}
                    disabled={isLoading}
                    style={{
                      flex: "0 0 auto",
                      padding: "10px 24px",
                      fontSize: "14px",
                      fontWeight: "600",
                      border: environment === "prod" ? "none" : "2px solid #dc3545",
                      borderRadius: "6px",
                      backgroundColor: environment === "prod" ? "#dc3545" : "transparent",
                      color: environment === "prod" ? "white" : "#dc3545",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    🚀 Production
                  </button>
                </div>
              </div>

              {/* Form Fields */}
              <div style={styles.formGrid}>
                <div style={styles.formColumn}>
                  <TextField
                    fullWidth
                    label="NUI (Business ID)"
                    type="number"
                    value={nui}
                    onChange={(e) => setNui(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>
                <div style={styles.formColumn}>
                  <TextField
                    fullWidth
                    label="Numri i Fiskalizimit"
                    type="number"
                    value={fiscalizationNumber}
                    onChange={(e) => setFiscalizationNumber(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>
                <div style={styles.formColumn}>
                  <TextField
                    fullWidth
                    label="POS ID"
                    type="number"
                    value={posId}
                    onChange={(e) => setPositId(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>
                <div style={styles.formColumn}>
                  <TextField
                    fullWidth
                    label="Branch ID"
                    type="number"
                    value={branchIdValue}
                    onChange={(e) => setBranchIdValue(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>
                <div style={styles.formColumn}>
                  <TextField
                    fullWidth
                    label="Application ID"
                    type="number"
                    value={applicationId}
                    onChange={(e) => setApplicationId(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>
                <div style={styles.formColumn}>
                  <TextField
                    fullWidth
                    label="SEF Code"
                    type="text"
                    value={sefCode}
                    onChange={(e) => setSefCode(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>
              </div>

              {/* Verification Message */}
              {verificationMessage && (
                <div
                  style={{
                    marginTop: "20px",
                    padding: "15px",
                    borderRadius: "6px",
                    backgroundColor:
                      messageType === "success" ? "#e8f5e9" : "#ffebee",
                    borderLeft:
                      messageType === "success"
                        ? "4px solid #27ae60"
                        : "4px solid #e74c3c",
                    color: messageType === "success" ? "#27ae60" : "#e74c3c",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  {verificationMessage}
                </div>
              )}

              {/* Action Buttons */}
              <div style={styles.actionButtons}>
                <button
                  type="button"
                  onClick={() => setIsExpanded(false)}
                  disabled={isLoading}
                  style={styles.cancelButton}
                >
                  Anulo
                </button>
                {verificationCode ? (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setVerificationMessage("");
                        setMessageType("");
                      }}
                      disabled={isLoading}
                      style={styles.secondaryButton}
                    >
                      Provo Përsëri
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab(2)}
                      disabled={isLoading}
                      style={styles.primaryButton}
                    >
                      ➜ Vazhdo në Hapi 2
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={handleVerify}
                    disabled={isLoading}
                    style={styles.primaryButton}
                  >
                    {isLoading ? "Duke u përpunuar..." : "Verifiko me ATK"}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* TAB 2: Certificate Signing */}
          {activeTab === 2 && (
            <div style={styles.tabContent}>
              <h5 style={styles.tabTitle}>Hapi 2: Nënshkrim të Sertifikatës</h5>
              <p style={styles.tabDescription}>
                Kliko butonin më poshtë për të nënshkruar sertifikatën me ATK. Fushat do të mbushen automatikisht.
              </p>

              <div style={styles.formGrid}>
                <div style={styles.formColumn}>
                  <TextField
                    fullWidth
                    label="Sertifikata e Nënshkruar"
                    multiline
                    rows={4}
                    value={signedCertificate}
                    disabled={true}
                    placeholder="Do të plotësohet automatikisht..."
                  />
                </div>
                <div style={styles.formColumn}>
                  <TextField
                    fullWidth
                    label="Çelësi Privat"
                    multiline
                    rows={4}
                    value={privateKeyPem}
                    disabled={true}
                    placeholder="Do të plotësohet automatikisht..."
                  />
                </div>
              </div>

              {/* CSR Message */}
              {csrMessage && (
                <div
                  style={{
                    marginTop: "20px",
                    padding: "15px",
                    borderRadius: "6px",
                    backgroundColor:
                      csrMessageType === "success" ? "#e8f5e9" : "#ffebee",
                    borderLeft:
                      csrMessageType === "success"
                        ? "4px solid #27ae60"
                        : "4px solid #e74c3c",
                    color: csrMessageType === "success" ? "#27ae60" : "#e74c3c",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  {csrMessage}
                </div>
              )}

              <div style={styles.actionButtons}>
                <button
                  type="button"
                  onClick={() => setActiveTab(1)}
                  disabled={isLoading}
                  style={styles.secondaryButton}
                >
                  Kthehu Prapa
                </button>
                {signedCertificate && privateKeyPem ? (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setCSRMessage("");
                        setCSRMessageType("");
                      }}
                      disabled={isLoading}
                      style={styles.secondaryButton}
                    >
                      Provo Përsëri
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab(3)}
                      disabled={isLoading}
                      style={styles.primaryButton}
                    >
                      ➜ Vazhdo në Hapi 3
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={handleSignCSR}
                    disabled={isLoading}
                    style={styles.primaryButton}
                  >
                    {isLoading ? "Duke u përpunuar..." : "Nënshkruaj Sertifikatën"}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* TAB 3: Completion */}
          {activeTab === 3 && (
            <div style={styles.tabContent}>
              <h5 style={styles.tabTitle}>Hapi 3: Përfundim</h5>
              <p style={styles.tabDescription}>
                Të dhënat janë gati. Kliko "Përfundo" për të mbushur fushat automatikisht.
              </p>

              <div style={styles.successBox}>
                <p style={{ color: "#27ae60", fontWeight: "600", marginBottom: "10px" }}>
                  ✅ Onboarding-u u krye me sukses!
                </p>
                <p>Të dhënat e mëposhtme do të mbushen në formën kryesore:</p>
                <ul style={{ marginTop: "10px", paddingLeft: "20px" }}>
                  <li>ATK POS ID: {posId}</li>
                  <li>ATK Application ID: {applicationId}</li>
                  <li>ATK Fiscalization Number: {fiscalizationNumber}</li>
                  <li>ATK SEF Code: {sefCode}</li>
                  <li>Signed Certificate: {signedCertificate.substring(0, 50)}...</li>
                  <li>Private Key: {privateKeyPem.substring(0, 50)}...</li>
                </ul>
              </div>

              <div style={styles.actionButtons}>
                <button
                  type="button"
                  onClick={handleReset}
                  style={styles.secondaryButton}
                >
                  Filloj Përsëri
                </button>
                <button
                  type="button"
                  onClick={handleFinish}
                  style={styles.successButton}
                >
                  ✅ Përfundo Onboarding-un
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    marginTop: "20px",
    marginBottom: "20px",
  },
  toggleButton: {
    width: "100%",
    padding: "15px 20px",
    backgroundColor: "#284945",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
  },
  expandedContent: {
    backgroundColor: "#f8f9fa",
    border: "2px solid #e9f1ed",
    borderRadius: "8px",
    marginTop: "15px",
    padding: "25px",
  },
  tabNav: {
    display: "flex",
    gap: "10px",
    marginBottom: "25px",
    borderBottom: "2px solid #d4e2db",
    paddingBottom: "15px",
  },
  tabButton: {
    padding: "10px 20px",
    backgroundColor: "transparent",
    border: "2px solid #d4e2db",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    color: "#666",
  },
  tabButtonActive: {
    backgroundColor: "#284945",
    color: "white",
    borderColor: "#284945",
  },
  tabButtonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  tabContent: {
    backgroundColor: "white",
    padding: "20px",
    borderRadius: "8px",
  },
  tabTitle: {
    color: "#284945",
    marginBottom: "10px",
    fontSize: "18px",
    fontWeight: "600",
  },
  tabDescription: {
    color: "#666",
    marginBottom: "20px",
    fontSize: "14px",
  },
  environmentBox: {
    marginBottom: "20px",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "15px",
    marginBottom: "20px",
  },
  formColumn: {
    display: "flex",
  },
  infoBox: {
    backgroundColor: "#e9f1ed",
    padding: "15px",
    borderRadius: "6px",
    marginBottom: "20px",
    borderLeft: "4px solid #4E7B67",
  },
  successBox: {
    backgroundColor: "#d4edda",
    border: "1px solid #c3e6cb",
    color: "#155724",
    padding: "15px",
    borderRadius: "6px",
    marginBottom: "20px",
  },
  actionButtons: {
    display: "flex",
    gap: "10px",
    justifyContent: "flex-end",
    marginTop: "20px",
  },
  primaryButton: {
    padding: "10px 20px",
    backgroundColor: "#284945",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
  secondaryButton: {
    padding: "10px 20px",
    backgroundColor: "#d4e2db",
    color: "#284945",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
  cancelButton: {
    padding: "10px 20px",
    backgroundColor: "#f5f5f5",
    color: "#666",
    border: "1px solid #d4e2db",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
  successButton: {
    padding: "10px 20px",
    backgroundColor: "#27ae60",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
};

export default ATKOnboardingWizard;
